import type { SearchRequest, SearchResponse } from '../../LookupField/services/api.types';
import type { DocumentHeaderData } from '../MasterDocument.types';
import type { MasterDocumentTemplate } from '../template/MasterDocumentTemplate.types';
import type { ApiService } from './ApiService.types';
import type {
  IMasterDocumentAdapter,
  DocumentState,
  SaveResult,
  SaveLineResult,
  DeleteLineResult,
  DeleteLineOptions,
  TransitionValidationResult,
  TransitionResult,
  ActionResult,
  ConflictInfo,
  DocumentListRequest,
  DocumentListResponse,
  CurrencyInfo,
  StatusHistoryEntry,
} from './MasterDocumentAdapter.types';
import type { DocumentListCompany } from '../MasterDocument.types';
import { createCurrencyFormatter } from '../formatters';

// ════════════════════════════════════════════════════════════
// HTTP HELPERS
// ════════════════════════════════════════════════════════════

class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function resolveHeaders(apiService: ApiService): Promise<Record<string, string>> {
  const headers = await apiService.getHeaders();
  return {
    'Content-Type': 'application/json',
    ...headers,
  };
}

async function request<T>(
  apiService: ApiService,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const url = `${apiService.baseUrl}${path}`;
  const headers = await resolveHeaders(apiService);

  const res = await fetch(url, {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
  });

  // 204 No Content
  if (res.status === 204) return undefined as T;

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    // Let consumer handle error globally
    if (apiService.onError?.(res.status, data)) {
      throw new ApiError('Error manejado por onError', res.status, data);
    }
    const errorField = (data as Record<string, unknown>)?.error;
    const msg =
      typeof errorField === 'string'
        ? errorField
        : errorField && typeof errorField === 'object' && 'message' in errorField && typeof (errorField as { message: unknown }).message === 'string'
          ? (errorField as { message: string }).message
          : res.statusText;
    throw new ApiError(msg, res.status, data);
  }

  return data as T;
}

// ════════════════════════════════════════════════════════════
// FORMATTING HELPERS
// ════════════════════════════════════════════════════════════

function createFormatCurrency(locale: string, currency: string) {
  // Regla única del kit: un monto entero se muestra con los decimales propios de
  // la moneda y uno con decimales nunca los pierde (ver `createCurrencyFormatter`).
  return createCurrencyFormatter(locale, currency);
}

function createFormatDate(locale: string) {
  return (value: string) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
  };
}

function createFormatNumber(locale: string) {
  const fmt = new Intl.NumberFormat(locale);
  return (value: number) => fmt.format(value);
}

// ════════════════════════════════════════════════════════════
// FACTORY
// ════════════════════════════════════════════════════════════

/**
 * Crea un adapter por defecto que usa los endpoints estándar
 * definidos en contratos_masterdocument_api.md.
 *
 * @param apiService — Servicio HTTP con baseUrl y headers de autenticación
 * @param template — Template del tipo de documento (para extraer documentClass)
 *
 * @example
 * const adapter = createDefaultAdapter(
 *   {
 *     baseUrl: 'https://api.siesa.com/api/v1',
 *     getHeaders: () => ({
 *       'Authorization': `Bearer ${token}`,
 *       'X-Company-Id': companyId,
 *     }),
 *     locale: 'es-CO',
 *     currency: 'COP',
 *   },
 *   template,
 * );
 */
/**
 * Crea un adapter por defecto que usa los endpoints estándar
 * definidos en contratos_masterdocument_api.md.
 *
 * Convención de rutas:
 * - Documento existente: `/documents/{id}` donde {id} es el UUID v7 (PK).
 *   No necesita documentClass porque el UUID es globalmente único.
 * - Operaciones de clase (template, creación, listado): `/documents/{documentClass}/...`
 *   Estas NO operan sobre un documento existente, necesitan la clase.
 *
 * @param apiService — Servicio HTTP con baseUrl y headers de autenticación
 * @param template — Template del tipo de documento (para extraer documentClass en operaciones de clase)
 */
export function createDefaultAdapter(
  apiService: ApiService,
  template: MasterDocumentTemplate,
): IMasterDocumentAdapter {
  const docClass = template.identity.documentClass;
  const locale = apiService.locale ?? 'es-CO';
  const currency = apiService.currency ?? 'COP';

  // Rutas para operaciones sobre un documento existente (por UUID)
  const docPath = (id: string) => `/documents/${id}`;

  // Rutas para operaciones de clase (template, creación, listado)
  const classPath = `/documents/${docClass}`;

  return {
    // ── Fetcher (LookupField / searchers) ──────────
    fetcher: async <TRecord = Record<string, unknown>>(
      entity: string,
      req: SearchRequest,
    ): Promise<SearchResponse<TRecord>> => {
      return request<SearchResponse<TRecord>>(
        apiService,
        'POST',
        `/entities/${entity}/search`,
        req,
      );
    },

    // ── CRUD Documento ─────────────────────────────
    // Todas las operaciones sobre documento existente usan UUID (PK)
    getDocument: (id: string): Promise<DocumentState> => {
      return request<DocumentState>(apiService, 'GET', docPath(id));
    },

    saveHeader: (id: string, header: DocumentHeaderData): Promise<SaveResult> => {
      return request<SaveResult>(apiService, 'PUT', `${docPath(id)}/header`, {
        headerValues: header,
      });
    },

    // El kit inyecta `_row_version` en `data` al editar una línea existente, por
    // lo que el token de concurrencia viaja en el body sin cache del consumidor.
    saveLine: (
      id: string,
      lineId: string | null,
      data: Record<string, unknown>,
    ): Promise<SaveLineResult> => {
      const lid = lineId ?? '_new';
      return request<SaveLineResult>(
        apiService,
        'PUT',
        `${docPath(id)}/lines/${lid}`,
        data,
      );
    },

    // `options.rowVersion`/`headerRowVersion` los provee el kit desde el estado.
    // Se envían como `_row_version` (línea) y `_rowVersion` (cabecera) en el body
    // del DELETE para que el backend valide la concurrencia de ambos.
    deleteLine: (id: string, lineId: string, options?: DeleteLineOptions): Promise<DeleteLineResult | void> => {
      const body: Record<string, string> = {};
      if (options?.rowVersion != null) body._row_version = options.rowVersion;
      if (options?.headerRowVersion != null) body._rowVersion = options.headerRowVersion;
      return request<DeleteLineResult | void>(
        apiService,
        'DELETE',
        `${docPath(id)}/lines/${lineId}`,
        Object.keys(body).length > 0 ? body : undefined,
      );
    },

    // ── Transiciones de estado ─────────────────────
    validateTransition: (
      id: string,
      to: string,
    ): Promise<TransitionValidationResult> => {
      return request<TransitionValidationResult>(
        apiService,
        'POST',
        `${docPath(id)}/transitions/validate`,
        { to },
      );
    },

    // `serverVersion` viaja en el body como `_rowVersion` para que execute-process
    // valide el `row_version` del header. Un desfase responde 409
    // (`OPTIMISTIC_LOCK_VIOLATION`); `request` lo lanza y el reducer lo trata como
    // conflicto duro. La respuesta incluye el nuevo `serverVersion` del documento.
    executeTransition: (id: string, to: string, _docState: string, serverVersion?: string): Promise<TransitionResult> => {
      return request<TransitionResult>(
        apiService,
        'POST',
        `${docPath(id)}/transitions`,
        serverVersion != null ? { to, _rowVersion: serverVersion } : { to },
      );
    },

    // ── Acciones ───────────────────────────────────
    executeAction: (
      actionKey: string,
      documentState: DocumentState,
    ): Promise<ActionResult> => {
      return request<ActionResult>(
        apiService,
        'POST',
        `${docPath(documentState.documentId!)}/actions/${actionKey}`,
      );
    },

    // ── FactBox data ───────────────────────────────
    getFactBoxData: (
      id: string,
      sectionKey: string,
    ): Promise<Record<string, unknown>> => {
      return request<Record<string, unknown>>(
        apiService,
        'GET',
        `${docPath(id)}/factbox/${sectionKey}`,
      );
    },

    // ── Historial de transiciones (OP1) ───────────────
    // GET /api/v1/documents/{id}/historial-estados
    // El backend devuelve { entries: StatusHistoryEntry[] } ordenado DESC por changedAt.
    // El adapter desempaqueta entries para que el componente reciba el array directamente.
    getStatusHistory: async (id: string): Promise<StatusHistoryEntry[]> => {
      const res = await request<{ entries: StatusHistoryEntry[] }>(
        apiService,
        'GET',
        `${docPath(id)}/historial-estados`,
      );
      return res.entries ?? [];
    },

    // ── Lista de documentos (usa documentClass, no UUID) ──
    //
    // Este adapter es MONO-CLASE por diseño: la clase va en la ruta
    // (`/documents/{documentClass}/list`) y sale del template, que solo declara
    // una. Por eso `documentClasses` (el filtro multi-clase que envía
    // `MasterDocumentPage` cuando la superficie sirve a varias) se descarta junto
    // con `documentClass` en lugar de reenviarse: mandarlo en el body de un
    // endpoint que ya está acotado a una clase sería un request contradictorio.
    // Un visor compartido de varias clases necesita un adapter propio.
    listDocuments: (req: DocumentListRequest): Promise<DocumentListResponse> => {
      const { filters } = req;
      // Extraer las claves reservadas de filters (no son campos de primer nivel)
      const { documentClass: _dc, documentClasses: _dcs, operationCenterId: _co, ...cleanFilters } =
        (filters ?? {}) as Record<string, unknown>;
      return request<DocumentListResponse>(apiService, 'POST', `${classPath}/list`, {
        page: req.page,
        pageSize: req.pageSize,
        companyId: req.companyId ?? null,
        search: req.search ?? null,
        stateFilter: req.stateFilter ?? null,
        filters: Object.keys(cleanFilters).length > 0 ? cleanFilters : null,
        orderBy: req.orderBy ?? null,
      });
    },

    // ── Lista de compañías (GET /companies) ───────
    listCompanies: async (): Promise<DocumentListCompany[]> => {
      const res = await request<{ data: Record<string, unknown>[] }>(
        apiService, 'POST', '/entities/companies/search',
        { page: 1, pageSize: 100, fields: [] },
      );
      return (res.data ?? []).map((item) => ({
        uuid: String(item.Id ?? item.id ?? ''),
        code: String(item.code ?? item.Id ?? item.id ?? ''),
        name: String(item.name ?? item.nombre ?? ''),
      }));
    },

    // ── Detección de conflictos ────────────────────
    checkConflict: (
      id: string,
      lastModified: string,
    ): Promise<ConflictInfo | null> => {
      return request<ConflictInfo | null>(
        apiService,
        'GET',
        `${docPath(id)}/conflict?serverVersion=${encodeURIComponent(lastModified)}`,
      );
    },

    // ── Aprobación ─────────────────────────────────
    approve: (id: string): Promise<void> => {
      return request<void>(apiService, 'POST', `${docPath(id)}/approve`);
    },

    reject: (id: string, reason?: string): Promise<void> => {
      return request<void>(apiService, 'POST', `${docPath(id)}/reject`, { reason });
    },

    // ── Multimoneda ────────────────────────────────
    listCurrencies: (onlyActive = true): Promise<CurrencyInfo[]> => {
      const qs = onlyActive ? '?onlyActive=true' : '';
      return request<CurrencyInfo[]>(apiService, 'GET', `/currencies${qs}`);
    },

    getExchangeRate: (
      currencyCode: string,
    ): Promise<{ rate: number; label: string }> => {
      return request<{ rate: number; label: string }>(
        apiService,
        'GET',
        `/currencies/${currencyCode}/rate`,
      );
    },

    changeCurrency: (id: string, currencyCode: string): Promise<void> => {
      return request<void>(
        apiService,
        'PUT',
        `${docPath(id)}/currency`,
        { currencyCode },
      );
    },

    // ── Formateo (client-side) ─────────────────────
    formatCurrency: createFormatCurrency(locale, currency),
    formatDate: createFormatDate(locale),
    formatNumber: createFormatNumber(locale),

    // ── Fetcher genérico (DocumentModalList) ───────
    genericFetch: (method: string, path: string, body?: unknown): Promise<unknown> => {
      return request<unknown>(apiService, method, path, body);
    },
  };
}
